import { Tag } from '@dev-spendesk/grapes';
import React, { useEffect, useId, useState } from 'react';

import { useFetchKycRemediation } from 'modules/invoices/hooks/useFetchKycRemediation';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { LocalStorageKey } from 'src/core/constants/storage';
import { useUser } from 'src/core/modules/app/hooks/useUser';

import styles from './KycRemediationBanner.module.css';
import { KycRemediationButton } from './KycRemediationButton';

export type Status = 'draft' | 'open' | 'approved' | 'rejected';

const variant = 'neutral';

/**
 * Banner to inform a company missing required KYC details about the upcoming
 * feature of paying suppliers worldwide directly from Spendesk.
 *
 * It prompts the user to update their company details in order to be ready
 * for the launch of this feature.
 *
 */
export const KycRemediationBanner = () => {
  const titleId = useId();
  const descriptionId = useId();
  const { t } = useTranslation('global');
  const user = useUser();

  const isUserAoOrOo = user.is_account_owner || user.is_organisation_owner;
  const isUserController = user.is_controller;
  const hasEEAInternationalWiretransferFeature = useFeature(
    FEATURES.TMP_EEA_INTERNATIONAL_WIRE_TRANSFER,
  );
  const shouldShowBannerForUser = isUserAoOrOo || isUserController;
  const fetchKycRemediationResult = useFetchKycRemediation({
    isEnabled:
      shouldShowBannerForUser && hasEEAInternationalWiretransferFeature,
  });
  const status =
    (fetchKycRemediationResult?.status === 'success' &&
      fetchKycRemediationResult?.data?.status) ||
    undefined;
  const isRemediationInFinalState =
    status && ['approved', 'rejected'].includes(status);

  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const isDismissedLocalStorage = localStorage.getItem(
      LocalStorageKey.KycRemediationBannerControllerDismissed,
    );
    if (isDismissedLocalStorage) {
      setIsDismissed(true);
    }
  }, []);

  const handleUpdateKycDetails = () => {
    if (fetchKycRemediationResult.status !== 'success') {
      return;
    }
    window.open(fetchKycRemediationResult.data.onboardingUrl, '_blank');
  };

  if (
    !hasEEAInternationalWiretransferFeature ||
    !status ||
    !shouldShowBannerForUser ||
    isRemediationInFinalState ||
    isDismissed
  ) {
    return null;
  }
  // in case of controllers we don't want to show the banner if the status is open (Under review)
  if (status === 'open' && !isUserAoOrOo) {
    return null;
  }

  const handleCloseClick = () => {
    localStorage.setItem(
      LocalStorageKey.KycRemediationBannerControllerDismissed,
      'true',
    );
    setIsDismissed(true);
  };

  return (
    <aside
      className={styles.banner}
      data-variant={variant}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <div className={styles.container}>
        <h1 className={styles.title} id={titleId}>
          <span>
            {t('invoices.transfer.kycRemediationBanner.title')}
            {status === 'draft' ? (
              <Tag className="ml-12" variant="ocean">
                {t('misc.new')}
              </Tag>
            ) : (
              <Tag
                className="ml-12"
                variant="info"
                iconName="circle-information"
              >
                {t('invoices.transfer.kycRemediationBanner.tagText.open')}
              </Tag>
            )}
          </span>
        </h1>
        <p className={styles.description} id={descriptionId}>
          <p className="white-space-pre-wrap">
            {t('invoices.transfer.kycRemediationBanner.body1')}
            <br />
            <br />
            {isUserAoOrOo
              ? t('invoices.transfer.kycRemediationBanner.body2')
              : t('invoices.transfer.kycRemediationBanner.body2Controller')}
            <br />
          </p>
        </p>
        {status === 'draft' || status === 'open' ? (
          <KycRemediationButton
            handleCloseClick={handleCloseClick}
            handleUpdateClick={handleUpdateKycDetails}
            showUpdateButton={isUserAoOrOo}
          />
        ) : null}
      </div>
    </aside>
  );
};
